import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { ROW_HIGHLIGHT_ANIMATION } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/table';
import {
	getTransactionsTableColumns,
	INTEREST_COLUMN_ID,
	TransactionCounts,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/transactionsTableColumns';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import {
	useTranslationCapManage,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { transactionMutationError } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/transactionMutationError.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatOneSchemaDateToIsoString } from '@dealstack/shared/lib/format/dates/date';
import { Button } from '@mantine/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
	bulkUploadTransactions,
	createOneSchemaTransactionsSession,
	deleteTransaction,
	getBulkUploadStatus,
	getTransactionsList,
	restoreTransactionRequest,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useMipsByDealId } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { OneSchemaTemplateKey } from '@dealstack/shared/lib/frontend/common-ui/oneSchemaTemplateKey.ts';
import {
	GetTransactionRes,
	PermissionRole,
	TaskStatus,
	TransactionListQueryDto,
} from '@dealstack/shared/lib/generated';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import {
	initialSorting,
	mapTableFilterToDto,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/utils.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import {
	OneSchemaBulkUploadErrorRecord,
	OneSchemaBulkUploadTransaction,
	OneSchemaConfigViewModel,
	TransactionTableFilter,
} from './types';

export const ONE_SCHEMA_WARNING_ERROR_CODE = 3002;

export const useTransactionsController = () => {
	const tCapManage = useTranslationCapManage();
	const tDefault = useTranslationDefault();
	const capManageFlowController = useContext(CapManageControllerContext);
	const { capForceDeleteFlag } = capManageFlowController;
	const userIsAdmin = capForceDeleteFlag;

	const oneSchemaUserConfig = useAppSelector((state) => state.user.data?.oneSchemaConfig);
	const [oneSchemaConfig, setOneSchemaConfig] = useState<OneSchemaConfigViewModel>({
		clientId: '',
		complex: false,
		jwt: '',
		template: OneSchemaTemplateKey.CAP_TRANSACTIONS_UPLOAD,
	});
	const {
		selectedEntityData,
		selectedEntityName,
		selectedEntityRegNumber,
		selectedEntityId,
		dealId,
		refetchPersons,
		personsSelectOptions,
	} = useContext(CapManageControllerContext);

	const { hasPermission: hasEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EDITOR,
	});

	const { hasPermission: hasAdminPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.ADMIN,
	});

	const { hasPermission: hasExportViewerPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EXPORT_VIEWER,
	});

	const { deal, refreshCapDetails } = useContext(DealContext);

	const [sessionId, setSessionId] = useState<string | null>(null);

	const bulkUploadTransactionsRequest = useMutation<
		Awaited<ReturnType<typeof bulkUploadTransactions>>,
		{ error?: { message?: string } },
		Parameters<typeof bulkUploadTransactions>[0]
	>({
		mutationFn: bulkUploadTransactions,
	});
	const deleteTransactionRequest = useMutation({
		mutationFn: deleteTransaction,
	});
	const createOneSchemaSessionRequest = useMutation({
		mutationFn: createOneSchemaTransactionsSession,
	});
	const getSecuritiesRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
				personId: selectedEntityId,
			}),
		],
		queryFn: ({ signal }) =>
			getPersonSecurities(
				formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
					personId: selectedEntityId,
				}),
				undefined,
				signal
			),
		enabled: !!selectedEntityId,
	});

	const getMipsRequest = useMipsByDealId(dealId);

	const restoreTransactionsRequest = useMutation({
		mutationFn: (id: string) => restoreTransactionRequest({ id }),

		onSuccess: () => {
			showToastV2({
				message: tCapManage('toasts.errors.transaction_restore_success'),
			});
			transactionsListRefetch();
			return Promise.all([refetchPersons(), refreshCapDetails()]);
		},

		// CAP-2039: a mutation-level onError REPLACES ApiLayer's code-derived default, so taking no
		// error argument here is what turned every reasoned refusal into "restore failed". A restore
		// is refused for reasons the user can act on - a lone single-title leg (120249), a security
		// that has since been deleted - and none of them reached the screen.
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.transaction_restore_fail')
			),
	});

	const restoreTransaction = (transactionId: string) => {
		restoreTransactionsRequest.mutate(transactionId);
	};

	const bulkUploadStatusRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.CAP_TRANSACTION_BULK_UPLOAD_SESSIONID_STATUS_GET, {
				sessionId: sessionId || '',
			}),
		],
		queryFn: ({ signal }) =>
			getBulkUploadStatus(
				formatUnicorn(API_PATHS.CAP_TRANSACTION_BULK_UPLOAD_SESSIONID_STATUS_GET, {
					sessionId: sessionId || '',
				}),
				signal
			),
		enabled: !!sessionId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (
				data?.status === TaskStatus.DONE ||
				data?.status === TaskStatus.ERROR ||
				!!query.state.error
			) {
				return false;
			}

			return 1000;
		},
	});

	useEffect(() => {
		if (bulkUploadStatusRequest.data?.status === TaskStatus.DONE) {
			transactionsListRefetch();
			void refetchPersons();
			void refreshCapDetails();
		}
	}, [bulkUploadStatusRequest.data]);

	const [showOneSchemaModal, setShowOneSchemaModal] = useState(false);
	const [showCreateNewTransactionDrawer, setShowCreateNewTransactionDrawer] = useState(false);
	const [showSplitTransactionDrawer, setShowSplitTransactionDrawer] = useState(false);
	const [showEditTransactionDrawer, setShowEditTransactionDrawer] = useState(false);
	const [showPairTransactionDrawer, setShowPairTransactionDrawer] = useState(false);
	const [splitTransaction, setSplitTransaction] = useState(null as GetTransactionRes | null);
	const [editTransaction, setEditTransaction] = useState(null as GetTransactionRes | null);
	const [pairTransaction, setPairTransaction] = useState(null as GetTransactionRes | null);
	const [transactionToReorder, setTransactionToReorder] = useState(
		null as GetTransactionRes | null
	);
	const [transactionToEdit, setTransactionToEdit] = useState<GetTransactionRes>();
	const [transactionIdToDelete, setTransactionIdToDelete] = useState<string>();
	const [showBulkUploadResultDrawer, setShowBulkUploadResultDrawer] = useState(false);
	const [showDeletedTable, setShowDeletedTable] = useState(false);
	const [transactionCounts, setTransactionCounts] = useState<null | TransactionCounts>(null);

	const mipsSelectOptions = useMemo(() => {
		return (getMipsRequest.data || [])
			.filter((mip) => mip.state === 'ACTIVE' && mip.programmeName)
			.map((mip) => ({
				label: mip.programmeName || '',
				value: mip.id,
			}));
	}, [getMipsRequest.data]);

	// CAP-1062 / CAP-1976: the legal/beneficial "Interest" column is dropped entirely (from the table
	// and the column picker) unless nominee structures are live for this deal — the feature flag AND
	// the deal's master switch. A deal with the switch off has one interest everywhere, so the column
	// would be a column of dashes.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	const showInterestColumn = nomineeStructuresEnabled && !!deal?.hasNomineeStructure;

	const columns = useMemo(() => {
		return getTransactionsTableColumns(
			tCapManage,
			selectedEntityData?.entityCurrency,
			setShowSplitTransactionDrawer,
			setEditTransaction,
			setShowEditTransactionDrawer,
			setSplitTransaction,
			setShowPairTransactionDrawer,
			setPairTransaction,
			setTransactionToReorder,
			restoreTransaction,
			!showDeletedTable,
			true,
			transactionCounts,
			getSecuritiesRequest.data?.securities,
			personsSelectOptions,
			mipsSelectOptions,
			hasEditorPermission
		).filter((column) => showInterestColumn || column.id !== INTEREST_COLUMN_ID);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		transactionCounts,
		selectedEntityData?.entityCurrency,
		getSecuritiesRequest.data?.securities,
		mipsSelectOptions,
		showDeletedTable,
		hasEditorPermission,
		showInterestColumn,
	]);

	const initialTableOptions: UseMantineTableDataOperationsPartialProps = {
		initialSorting,
	};
	const useStateFromUrlParams = true;
	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<GetTransactionRes, TransactionTableFilter>(
			columns,
			initialTableOptions,
			useStateFromUrlParams
		);

	const lastSelectedEntityIdRef = useRef<string | null>(null);
	const lastDataOperationsRef = useRef<typeof dataOperations>();

	const resetPagination = useCallback(() => {
		mantineTableDataOperations.setPagination({
			pageIndex: 0,
			pageSize: dataOperations.pagination?.take ?? 50,
		});
	}, [mantineTableDataOperations, dataOperations.pagination?.take]);

	useEffect(() => {
		const prevEntity = lastSelectedEntityIdRef.current;
		const prevOps = lastDataOperationsRef.current;

		const entityChanged = prevEntity && prevEntity !== selectedEntityId;

		const searchChanged = prevOps && dataOperations.search !== prevOps.search;
		const filterChanged =
			prevOps && JSON.stringify(dataOperations.filter) !== JSON.stringify(prevOps.filter);

		if (entityChanged) {
			mantineTableDataOperations.resetAll();
			resetPagination();
		} else if ((searchChanged || filterChanged) && dataOperations.pagination?.skip !== 0) {
			resetPagination();
		}

		lastSelectedEntityIdRef.current = selectedEntityId;
		lastDataOperationsRef.current = dataOperations;
	}, [dataOperations, selectedEntityId, resetPagination, mantineTableDataOperations]);

	const [transactionsRequestParams, setTransactionsRequestParams] =
		useState<TransactionListQueryDto>({
			personId: selectedEntityId,
			pagination: { take: 50, ...dataOperations.pagination },
			filter: dataOperations.filter || {},
			search: dataOperations.search,
			sortBy: dataOperations.sortBy,
			fetchDeleted: showDeletedTable,
		});

	const getTransactionsListRequest = useQuery({
		queryKey: ['', transactionsRequestParams],
		queryFn: () => getTransactionsList(transactionsRequestParams),
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		transactionsListRefetch(selectedEntityId, true);
	}, [selectedEntityId, dataOperations]);

	useEffect(() => {
		if (getTransactionsListRequest?.data) {
			const transactions = showDeletedTable
				? getTransactionsListRequest.data.deletedTransactions
				: getTransactionsListRequest.data.transactions;

			if (transactions && transactions.length > 0) {
				const counts = showDeletedTable
					? getTransactionsListRequest.data.deletedTransactionCounts
					: getTransactionsListRequest.data.transactionCounts;

				setTransactionCounts({
					uniqueDateCount: counts.uniqueDateCount,
					uniqueSecurityCount: counts.uniqueSecurityCount,
					uniqueTypeCount: counts.uniqueTypeCount,
					uniqueBuyerCount: counts.uniqueBuyerCount,
					uniqueSellerCount: counts.uniqueSellerCount,
					uniqueBoughtForCount: counts.uniqueBoughtForCount,
					uniqueSoldForCount: counts.uniqueSoldForCount,
					totalAmount: counts.totalAmount,
					totalPrice: counts.totalPrice,
				});
			} else {
				setTransactionCounts(null);
			}
		}
	}, [getTransactionsListRequest.data, showDeletedTable]);

	const transactionsListRefetch = useCallback(
		(entityId?: string, skipRefetch = false) => {
			setTransactionsRequestParams({
				personId: entityId || selectedEntityId,
				pagination: { take: 50, ...dataOperations.pagination },
				filter: mapTableFilterToDto(dataOperations.filter),
				search: dataOperations.search,
				sortBy: dataOperations.sortBy,
				fetchDeleted: showDeletedTable,
			});

			if (!skipRefetch) {
				void getTransactionsListRequest.refetch();
			}
		},
		[dataOperations, selectedEntityId, dealId]
	);

	const toggleDeletedView = (showDeleted: boolean) => {
		setShowDeletedTable(showDeleted);
		setTransactionsRequestParams((prev) => ({
			...prev,
			fetchDeleted: showDeleted,
		}));
	};

	useEffect(() => {
		const {
			oneSchemaClientId = '',
			oneSchemaJwt = '',
			complex = false,
		} = oneSchemaUserConfig || {};
		setOneSchemaConfig({
			clientId: oneSchemaClientId,
			complex,
			jwt: oneSchemaJwt,
			template: complex
				? OneSchemaTemplateKey.CAP_TRANSACTIONS_UPLOAD_COMPLEX
				: OneSchemaTemplateKey.CAP_TRANSACTIONS_UPLOAD,
		});
	}, [oneSchemaUserConfig]);

	const onBulkUploadTransactions = (
		{
			records,
			error_records,
		}: {
			records: OneSchemaBulkUploadTransaction[];
			error_records?: OneSchemaBulkUploadErrorRecord[];
		},
		sessionId: string
	) => {
		const filteredWarningRecords = (error_records || [])
			.filter((entry) => {
				const recordsWithErrors = Object.keys(entry.errors).reduce(
					(acc, curr) => [
						...acc,
						...(entry.errors[curr] || []).filter(
							(code) => code !== ONE_SCHEMA_WARNING_ERROR_CODE
						),
					],
					[]
				);

				return recordsWithErrors.length === 0;
			})
			.map((entry) => entry.data);

		const allTransactions = [...records, ...filteredWarningRecords];

		// Parse number that may have thousand separators (e.g. "4,624" -> 4624)
		const parseNumericValue = (value: unknown): number => {
			if (typeof value === 'number') {
				return value;
			}
			if (typeof value === 'string') {
				return Number(value.replace(/,/g, ''));
			}
			return 0;
		};

		const mappedTransactions = allTransactions.map((transaction) => ({
			type: transaction.type || undefined,
			amount: parseNumericValue(transaction.amount),
			price: parseNumericValue(transaction.price),
			date: formatOneSchemaDateToIsoString(transaction.date),
			fromMipName: transaction.fromMipName || null,
			toMipName: transaction.toMipName || null,
			securityType: transaction.securityType,
			source: transaction.source,
			destination: transaction.destination,
			soldFor: transaction.soldFor || null,
			boughtFor: transaction.boughtFor || null,
			securityName: transaction.securityName,
		}));

		bulkUploadTransactionsRequest.mutate(
			{
				entityId: selectedEntityId,
				transactions: mappedTransactions,
				oneSchemaSessionId: sessionId,
			},
			{
				onSuccess: (response) => {
					setSessionId(sessionId);
					setShowBulkUploadResultDrawer(true);

					const createdEntitiesNumber = response?.createdEntitiesCount;
					if (createdEntitiesNumber === 0) {
						showToastV2({
							message: tCapManage(
								'toasts.success.transactions_uploaded_successfully'
							),
						});
					}
				},
				onError: (error: { error?: { message?: string } }) => {
					showToastV2({
						state: 'error',
						message: error?.error?.message || '',
					});
				},
			}
		);
	};

	const onDelete = useCallback((transactionId: string) => {
		deleteTransactionRequest.mutate(
			{ transactionId },
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.transactions_deleted_successfully'),
					});
					transactionsListRefetch();
					void refetchPersons();
					void refreshCapDetails();
				},
			}
		);
	}, []);

	const tableRef = useRef<HTMLDivElement | null>(null);

	const onCreateTransactionModalClose = async (createdTransactionId?: string | Event) => {
		setShowCreateNewTransactionDrawer(false);
		if (!createdTransactionId) {
			return;
		}
		if (typeof createdTransactionId !== 'string') {
			return;
		}

		const updatedTransactionsResp = getTransactionsListRequest.data;
		const isCreatedTransactionExistOnCurrentPage = (
			updatedTransactionsResp?.transactions ?? []
		).some((row) => row.id === createdTransactionId);

		//we have to highlight created transaction if its on current page - D.Zghinnik 11.10.2023
		if (isCreatedTransactionExistOnCurrentPage) {
			//set timeout with 0 needed to run query selector after dom re rendered - D.Zghinnik 11.10.2023
			setTimeout(() => {
				const row = tableRef?.current?.querySelector(`tr[id="${createdTransactionId}"]`);

				row?.scrollIntoView({ block: 'start', behavior: 'smooth' });
				row?.animate(ROW_HIGHLIGHT_ANIMATION.steps, ROW_HIGHLIGHT_ANIMATION.options);
			}, 0);
			return;
		}

		showToastV2({
			title: tCapManage('toasts.success.transactions_created_successfully'),
			message: (
				<Button
					onClick={() => {
						mantineTableDataOperations.setGlobalSearchString(createdTransactionId);
						mantineTableDataOperations.setShowGlobalSearchStringInput(true);
					}}
				>
					{tCapManage('toasts.buttons.jump_to_record')}
				</Button>
			),
		});
	};

	const showTable =
		mantineTableDataOperations.isFiltersApplied ||
		!!dataOperations.search ||
		!!getTransactionsListRequest.data?.transactions?.length ||
		showDeletedTable ||
		getTransactionsListRequest.isLoading ||
		getTransactionsListRequest.isFetching;

	const deletedCountRequestParams: TransactionListQueryDto = {
		personId: selectedEntityId,
		pagination: { take: 1, skip: 0 },
		fetchDeleted: true,
	};

	const getDeletedCountRequest = useQuery({
		queryKey: ['deletedCount', deletedCountRequestParams],
		queryFn: () => getTransactionsList(deletedCountRequestParams),
		enabled: !showTable && hasAdminPermission,
	});

	return {
		tableRef,
		selectedEntityName,
		selectedEntityRegNumber,
		dealId,
		selectedEntityId,
		dataOperations,
		onBulkUploadTransactions,
		transactionsListRefetch,
		oneSchemaConfig,
		getTransactionsListRequest,
		getSecuritiesRequest,
		bulkUploadTransactionsRequest,
		securities: getSecuritiesRequest.data?.securities,
		mantineTableDataOperations,
		columns,
		onDelete,
		deleteTransactionRequest,
		isTableLoading: getTransactionsListRequest.isFetching || deleteTransactionRequest.isPending,
		showOneSchemaModal,
		setShowOneSchemaModal,
		showCreateNewTransactionDrawer,
		setShowCreateNewTransactionDrawer,
		transactionToEdit,
		setTransactionToEdit,
		transactionIdToDelete,
		setTransactionIdToDelete,
		onCreateTransactionModalClose,
		createOneSchemaSessionRequest,
		showTable,
		refreshCapDetails,
		showBulkUploadResultDrawer,
		setShowBulkUploadResultDrawer,
		transactionToReorder,
		showSplitTransaction: splitTransaction,
		pairTransaction,
		editTransaction,
		setTransactionToReorder,
		isLoading:
			getTransactionsListRequest.isLoading ||
			getSecuritiesRequest.isLoading ||
			bulkUploadTransactionsRequest.isPending,
		refetchPersons,
		statusResponse: bulkUploadStatusRequest.data,
		userIsAdmin,
		showEditTransactionDrawer,
		setShowEditTransactionDrawer,
		showSplitTransactionDrawer,
		setShowSplitTransactionDrawer,
		showPairTransactionDrawer,
		setShowPairTransactionDrawer,
		showDeletedTable,
		setShowDeletedTable,
		toggleDeletedView,
		restoreTransaction,
		selectedEntityData,
		hasEditorPermission,
		hasAdminPermission,
		hasExportViewerPermission,
		deletedTransactionCount: getDeletedCountRequest.data?.totalCountDeleted ?? 0,
	};
};
